import { Component, ElementRef, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CartActions, StateWithMultiCart } from '@spartacus/cart/base/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { AuthService, GlobalMessageService, GlobalMessageType, RoutingService, UserIdService } from '@spartacus/core';
import { Observable, combineLatest, of, timer } from 'rxjs';
import { concatMap, filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { CartExtend } from 'src/app/model/cart.model';
import { CartExtendService } from 'src/app/service/cart/cart-extend.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { CustomTranslationService } from 'src/app/service/language/custom-translation.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';

@Component({
  selector: 'app-quick-order-submit-modal',
  templateUrl: './quick-order-submit-modal.component.html',
  styleUrls: ['./quick-order-submit-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class QuickOrderSubmitModalComponent {
  entries: any;
  activeCartId: any;
  gtmDataLayer: any;
  userCurrent$: Observable<string> = this.userIdService.getUserId();
  showSpinner = false;
  cart: any;
  cartId: string;
  cart$: Observable<CartExtend>;

  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();

  isCustomTranslationsEnabled$: Observable<boolean> = combineLatest([this.activeCountry$, this.activeLanguage$]).pipe(
    switchMap(([activeCountry, activeLanguage]) =>
      this.customTranslationService.isCustomTranslationsEnabled$(activeCountry, activeLanguage, [
        'cart.quickOrder.submitModal.title',
        'cart.quickOrder.submitModal.continue',
      ])
    )
  );

  productCategories: any = {};

  constructor(
    private messageService: GlobalMessageService,
    private routingService: RoutingService,
    private store: Store<StateWithMultiCart>,
    private cartService: CartExtendService,
    private authService: AuthService,
    protected activeCartService: ActiveCartFacade,
    protected router: Router,
    private gtmService: GoogleTagManagerService,
    private countryService: ShimanoCountryService,
    private languageService: ShimanoLanguageService,
    private customTranslationService: CustomTranslationService,
    private userIdService: UserIdService,
    protected modalService: StandardModalService,
    protected el: ElementRef,
    protected vcr: ViewContainerRef
  ) {
    this.modalService
      .getModalData()
      .pipe(take(1))
      .subscribe((res) => {
        this.entries = res.entries;
        this.activeCartId = res.activeCartId;
        this.gtmDataLayer = res.gtmDataLayer;
      });
  }

  get userCurrent(): string {
    let userCurrent: string;
    this.userCurrent$.pipe(take(1)).subscribe((userId) => (userCurrent = userId));
    return userCurrent;
  }

  updateActiveCart(): void {
    this.cartService
      .getActiveCartId()
      .pipe(map((data) => data))
      .subscribe((code) => {
        this.store.dispatch(
          new CartActions.LoadCart({
            userId: this.userCurrent,
            cartId: code,
          })
        );
      })
      .unsubscribe();
  }

  submitEntries(entries: any): void {
    if (!this.showSpinner) {
      this.showSpinner = true;
      this.cartService
        .addToQuickOrder(entries, this.activeCartId)
        .pipe(
          map((response) => {
            this.cartId = response.code;
            return response;
          }),
          mergeMap(() => of(null)),
          take(1),
          switchMap(() =>
            timer(0, 2000).pipe(
              concatMap(() => this.cartService.getCartProcessStatus(this.cartId)),
              filter((stat) => stat.status === 'COMPLETED'),
              take(1)
            )
          )
        )
        .subscribe(
          (success) => {
            this.closeModal('close modal');
            this.messageService.add(
              { key: 'cart.quickOrder.submitModal.success' },
              GlobalMessageType.MSG_TYPE_CONFIRMATION
            );
            this.gtmAddToCart(this.gtmDataLayer);
            this.routingService.goByUrl('/cart');
            this.updateActiveCart();
          },
          (err) => {
            this.closeModal('close modal');
            this.messageService.add({ key: 'cart.quickOrder.submitModal.error' }, GlobalMessageType.MSG_TYPE_ERROR);
            this.routingService.goByUrl('/cart');
            this.updateActiveCart();
          }
        );
    }
  }

  gtmAddToCart(dataLayerDetails: any) {
    const productsDataLayer: any[] = [];
    let totalPrice = 0;
    dataLayerDetails.forEach((dataLayer: any) => {
      totalPrice = totalPrice + dataLayer.price;
      this.productCategories = {};
      dataLayer.item_categories[0].map((cat: any, i: any) => {
        const indexCount = i + 1;
        const label = `item_category${indexCount == 1 ? '' : indexCount}`;
        this.productCategories[label] = cat.name;
      });
      productsDataLayer.push({
        item_id: dataLayer.item_id,
        item_name: dataLayer.item_name,
        affiliation: dataLayer.affiliation,
        item_brand: dataLayer.item_brand,
        ...this.productCategories,
        price: dataLayer.price,
        quantity: dataLayer.quantity,
      });
    });
    const gtmTag = {
      event: GoogleTagManagerEvent.ADD_TO_CART,
      ecommerce: {
        currency: dataLayerDetails[0].currency,
        value: totalPrice.toFixed(2),
        items: productsDataLayer,
      },
    };
    if (dataLayerDetails) {
      this.gtmService.pushTag({
        ecommerce: null,
        search_term: '',
        results_page: '',
        affiliation: '',
        content_type: '',
        content_id: '',
        content_name: '',
      });
      this.gtmService.pushTag(gtmTag);
    }
  }

  customizeStylingButtons(activeLanguage: any): boolean {
    return ['de', 'es', 'pl', 'no', 'pt'].includes(activeLanguage);
  }

  setCustomContext(isCustomTranslationsEnabled: boolean, activeCountry: string, activeLanguage: string) {
    return isCustomTranslationsEnabled ? [activeCountry, activeLanguage].join('-') : null;
  }

  closeModal(info?: any) {
    this.modalService.closeDialog(info);
  }
}
